// Pure helpers for the Leads page (web/src/pages/Leads.tsx): URL filters, the state filter, and which lead a row's
// "Call" button opens. Migration 0006: leads in hand-pick-only states (Oklahoma) are never served by the queue, so the
// setter finds them here (filter by state) and opens each one by hand.
import type { MembershipStatus, StateRule } from '../../../src/shared/api';
import type { BusinessListItem } from './types';

/** Membership statuses that can't be dialed (the Leads drawer disables "Open in dialer" for them too). */
export const CLOSED_STATUSES: MembershipStatus[] = ['closed_lost', 'disqualified', 'bad_data', 'dnc', 'exhausted', 'meeting'];

/** A state filter value: a state_rules code such as 'US-OK' (what the server's `state` parameter takes). */
const STATE_CODE = /^(US|CA)-[A-Z]{2}$/;

/** `/leads?campaignId=3&state=US-OK` (the Dialer's hand-pick link) → the Leads page's starting filters. */
export function leadsFiltersFromSearch(search: string): { campaignId: number | null; state: string } {
  const p = new URLSearchParams(search);
  const id = Number(p.get('campaignId'));
  const state = (p.get('state') ?? '').trim().toUpperCase();
  return { campaignId: Number.isInteger(id) && id > 0 ? id : null, state: STATE_CODE.test(state) ? state : '' };
}

export interface StateOption { value: string; label: string }

/**
 * The state filter's options, US then Canada, each by name. Hand-pick-only states say so, since that is why a setter
 * filters by state; closed ones say they aren't called.
 */
export function stateFilterOptions(rules: StateRule[] | null | undefined): Array<{ group: string; options: StateOption[] }> {
  const list = Array.isArray(rules) ? rules : [];
  const label = (r: StateRule): string =>
    !r.callingAllowed ? `${r.name} (not called)` : r.handPickOnly ? `${r.name} (open by hand)` : r.name;
  const group = (country: 'US' | 'CA') => list
    .filter((r) => r.country === country && STATE_CODE.test(r.code))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((r) => ({ value: r.code, label: label(r) }));
  return [
    { group: 'United States', options: group('US') },
    { group: 'Canada', options: group('CA') },
  ].filter((g) => g.options.length > 0);
}

/** The hand-pick-only rule for a business's state, from the loaded rules (null when not hand-pick only). */
export function handPickRule(rules: StateRule[] | null | undefined, b: { country: string; stateProv: string }): StateRule | null {
  const code = `${b.country}-${(b.stateProv ?? '').toUpperCase()}`;
  return (Array.isArray(rules) ? rules : []).find((r) => r.code === code && r.handPickOnly) ?? null;
}

/** PJ 2026-10-28 ("I do not want it where people can't call meetings booked as sometimes we need a follow up with them to
 *  get them into the meeting"): a lead with a meeting booked can be opened and called; the dial gate warns "Meeting
 *  booked. Call anyway?" before it dials. Every other closed status still can't be opened. */
export function canOpenLead(status: MembershipStatus): boolean {
  return status === 'meeting' || !CLOSED_STATUSES.includes(status);
}

/**
 * The memberships a row's "Call" button can open: not closed (a meeting booked can, canOpenLead), in the filtered
 * campaign when one is picked, and the business isn't flagged do-not-contact or bad number. One → the button opens it;
 * several → the drawer lets the setter pick the campaign; none → no button.
 */
export function callableMemberships(row: BusinessListItem, campaignId: number | null): BusinessListItem['memberships'] {
  if (row.business.doNotContact || row.business.badNumber) return [];
  return row.memberships.filter((m) => canOpenLead(m.status) && (campaignId === null || m.campaignId === campaignId));
}
